import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MergePatientsComponent } from '@app/patients/merge-patients/merge-patients.component';
import { isNullOrUndefined } from '@app/shared/helpers';
import { Patient } from '@models/patient';
import { MasterOverlayService } from '@services/actionpanel.service';
import { EventsService } from '@services/events.service';
import { PatientService } from '@services/patient.service';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { toTitleCase } from '../../../shared/helpers';

@Component({
  selector: 'app-search-patient[sideNavExpanded]',
  templateUrl: './search-patient.component.html',
  styleUrls: ['./search-patient.component.less'],
})
export class SearchPatientComponent implements OnInit, OnDestroy {
  @Input() sideNavExpanded!: boolean;

  private unsub: Subject<void> = new Subject<void>();
  searchTerm = new Subject<string>();
  typeaheadSource!: Observable<Patient[] | null>;
  selectedPatientIds: Number[] = [];
  typeaheadControl = new FormControl('');
  typeaheadLoading = false;
  selectedFilter: 'name' | 'email' | 'phone' = 'name';
  showFilter = false;

  constructor(
    private patientService: PatientService,
    private masterOverlayService: MasterOverlayService,
    private router: Router,
    private mergePatientDialog: MatDialog,
    private eventService: EventsService
  ) {}

  ngOnInit(): void {
    this.typeaheadSource = this.typeaheadControl.valueChanges.pipe(
      tap(() => (this.typeaheadLoading = true)),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.selectedPatientIds = [])),
      mergeMap((token: string) => this.patientService.searchPatientByTerm(token, this.selectedFilter)),
      tap(() => (this.typeaheadLoading = false))
    );
  }

  selectPatientCheckbox(patientId: number, event: Event) {
    if ((event?.target as HTMLInputElement)?.checked) this.selectedPatientIds.push(patientId);
    else this.selectedPatientIds.splice(this.selectedPatientIds.indexOf(patientId), 1);
  }

  selectPatient(patientId: number) {
    this.typeaheadControl.setValue('');
    this.selectedPatientIds = [];
    this.eventService.closePanel();
    this.masterOverlayService.masterOverlayState(true);
    this.patientService.patientPanelPatient = null;
    this.router.navigate(['/schedule', { outlets: { 'action-panel': ['patient', patientId + '_patientprofiletab'] } }]);
  }

  mergePatients() {
    if (this.selectedPatientIds.length == 2) {
      const dialogRef = this.mergePatientDialog.open(MergePatientsComponent, {
        panelClass: 'custom-dialog-container',
        width: '1200px',
        height: 'calc(100dvh - 100px)',

        data: { patientId: this.selectedPatientIds[0], duplicatePatientId: this.selectedPatientIds[1] },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.unsub))
        .subscribe((result) => {
          if (result === true) {
            this.selectedPatientIds = [];
          }
        });
    }
  }

  formatResultString(item: Patient) {
    let token = this.typeaheadControl.value;
    let displayString = '';

    //Create the desired format when only "one word" is in the input
    if (token.indexOf(' ') == -1) {
      //Building the desired string output first
      displayString =
        toTitleCase(item.firstName) +
        ' ' +
        (item.nickName ? toTitleCase(item.nickName) + ' ' : '') +
        toTitleCase(item.lastName) +
        (item.birthDate
          ? ' ' + this.getBirthday(item.birthDate.toString()) + ' (' + this.getAge(item.birthDate.toString()) + ')'
          : '');
      if (this.selectedFilter == 'email' && item.email) {
        displayString = displayString.concat(' ' + item.email.toLowerCase());
      }
      if (this.selectedFilter == 'phone') {
        displayString = displayString.concat(
          (item.homeNumber ? ' H:' + item.homeNumber : '') + (item.mobileNumber ? ' M:' + item.mobileNumber : '')
        );
      }

      displayString = this.matchAndBold(displayString, token);
    } else {
      let tokensList = token.split(' ');
      let displayFirst = toTitleCase(item.firstName) + ' ' + (item.nickName ? toTitleCase(item.nickName) : '');
      let displayLast = toTitleCase(item.lastName);
      displayFirst = this.matchAndBold(displayFirst, tokensList[0]);
      if (tokensList[1]) displayLast = this.matchAndBold(displayLast, tokensList[1]);

      //We dont need to worry about email or phone as this will never find email or phone with split text (ie with a space " ")
      displayString =
        displayFirst +
        ' ' +
        displayLast +
        (item.birthDate
          ? ' ' + this.getBirthday(item.birthDate.toString()) + ' (' + this.getAge(item.birthDate.toString()) + ')'
          : '');
    }

    return displayString;
  }

  //Find all possible matches in text and return text with bolding the match
  private matchAndBold(text: string, token: string) {
    let tokenLen = token.length;
    let lowerText = text.toLowerCase();
    let lowerToken = token.toLowerCase();

    //Find all possible indexes of matching text
    let pos = 0;
    let result: number[] = [];
    while (lowerText.indexOf(lowerToken, pos) != -1) {
      if (result.length == 0) result.push(lowerText.indexOf(lowerToken, pos));
      else result.push(lowerText.indexOf(lowerToken, pos) + 17 * result.length); //Add 17*result.length is to account for the addition of <strong></strong>
      pos = lowerText.indexOf(lowerToken, pos) + 1;
    }

    //Bold all found matches
    for (let i = 0; i < result.length; i++) {
      text = `${text.substring(0, result[i])}<strong>${text.substring(
        result[i],
        result[i] + tokenLen
      )}</strong>${text.substring(result[i] + tokenLen)}`;
    }

    return text;
  }

  private getAge(DOB: string) {
    if (!isNullOrUndefined(DOB)) {
      const today = new Date();
      const birthDate = new Date(DOB);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } else {
      return 'N/A';
    }
  }

  private getBirthday(date: string): string {
    if (!isNullOrUndefined(date)) {
      return moment(date).format('YYYY-MM-DD');
    } else {
      return 'N/A';
    }
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
